import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import {
  Box,
  Checkbox,
  FormControlLabel,
  Grid,
  IconButton,
  TextField,
  Typography,
} from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs, { Dayjs } from 'dayjs';
import React, { useEffect, useState } from 'react';

interface ExtendedDatePickerProps {
  validUntil: Date | null;
  setValidUntil: (date: Date | null) => void;
  days: number;
  setDays: (days: number) => void;
  canDeactivateExpiration?: boolean;
}

const ExtendedDatePicker: React.FC<ExtendedDatePickerProps> = ({
  validUntil,
  setValidUntil,
  days,
  setDays,
  canDeactivateExpiration = false,
}) => {
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [lastUsedDays, setLastUsedDays] = useState(30);
  const [hasExpiration, setHasExpiration] = useState<boolean>(
    !canDeactivateExpiration || !!validUntil,
  );
  const validUntilDayjs = validUntil ? dayjs(validUntil) : null;

  useEffect(() => {
    if (validUntil && days === 0) {
      const today = dayjs().startOf('day');
      const selectedDate = dayjs(validUntil).startOf('day');
      const diffDays = selectedDate.diff(today, 'day');
      if (diffDays >= 0) setDays(diffDays);
    }
  }, [validUntil, days, setDays]);

  useEffect(() => {
    if (hasExpiration && days > 0) {
      setLastUsedDays(days);
    }
  }, [days, hasExpiration]);

  const handleChangeDays = (value: any) => {
    const numValue = parseInt(value);
    if (numValue < 0) return;
    setDays(numValue);
    const date = dayjs().startOf('day').add(numValue, 'day');
    setValidUntil(date.toDate());
  };

  const handleDateChange = (newDate: Dayjs | null) => {
    if (newDate) {
      const today = dayjs().startOf('day');
      const selectedDate = newDate.startOf('day');
      if (selectedDate.isBefore(today)) return;

      setValidUntil(selectedDate.toDate());
      const diffDays = selectedDate.diff(today, 'day');
      setDays(diffDays);
    } else {
      setValidUntil(null);
      setDays(0);
    }
  };

  const handleExpirationToggle = (checked: boolean) => {
    setHasExpiration(checked);
    if (checked) {
      const daysToUse = lastUsedDays > 0 ? lastUsedDays : 30;
      const defaultDate = dayjs().add(daysToUse, 'day').toDate();
      setValidUntil(defaultDate);
      setDays(daysToUse);
    } else {
      setValidUntil(null);
      setDays(0);
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Grid item xs={4} style={{ position: 'relative' }}>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          marginBottom={1}
        >
          <Typography fontWeight={600} variant="body1">
            Días para el vencimiento
          </Typography>
          {canDeactivateExpiration && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={hasExpiration}
                  onChange={(e) => handleExpirationToggle(e.target.checked)}
                  size="small"
                  sx={{
                    '&.Mui-checked': {
                      color: 'primary.main',
                    },
                  }}
                />
              }
              label={
                <Typography variant="caption" color="text.secondary">
                  Con vencimiento
                </Typography>
              }
              sx={{ margin: 0 }}
            />
          )}
        </Box>

        {hasExpiration ? (
          <>
            <Box display="flex" alignItems="center" gap={1}>
              <TextField
                autoFocus
                margin="dense"
                id="days-input"
                label="Días"
                type="number"
                value={days}
                onChange={(e) => handleChangeDays(e.target.value)}
                inputProps={{ min: 0 }}
                sx={{ flex: 1 }}
              />
              <IconButton
                onClick={() => setIsDatePickerOpen(!isDatePickerOpen)}
                sx={{
                  marginTop: 1,
                  color: 'primary.main',
                  '&:hover': {
                    backgroundColor: 'primary.light',
                    color: 'white',
                  },
                }}
              >
                <CalendarTodayIcon />
              </IconButton>
            </Box>

            <DatePicker
              open={isDatePickerOpen}
              onClose={() => setIsDatePickerOpen(false)}
              value={validUntilDayjs}
              onChange={handleDateChange}
              minDate={dayjs().startOf('day')}
              slotProps={{
                textField: { style: { display: 'none' } },
                popper: {
                  anchorEl: document.getElementById('days-input'),
                  placement: 'bottom-start',
                  modifiers: [
                    {
                      name: 'offset',
                      options: {
                        offset: [0, 16],
                      },
                    },
                  ],
                },
              }}
            />

            {validUntilDayjs && (
              <Typography
                marginTop={1}
                variant="body2"
                color="text.secondary"
                sx={{ fontStyle: 'italic' }}
              >
                Fecha seleccionada: {validUntilDayjs.format('DD/MM/YYYY')}
              </Typography>
            )}
          </>
        ) : (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              fontStyle: 'italic',
              padding: 1,
              backgroundColor: 'grey.50',
              borderRadius: 1,
              border: '1px dashed',
              borderColor: 'grey.300',
            }}
          >
            Sin fecha de vencimiento
          </Typography>
        )}
      </Grid>
    </LocalizationProvider>
  );
};

export default ExtendedDatePicker;
